/**
 * Tên role + các nhóm quyền dùng chung ở FE.
 *
 * Bản sao có chủ đích của `backend/app/core/roles.py` (ROLE_* và `BUSINESS_ROLES`).
 * Backend mới đổi guard `require_staff_or_admin` sang ALLOW-LIST; FE thì còn ba chỗ
 * nhốt riêng `accountant` bằng DENY-LIST (`!isAccountant`, `!isPrinter`), nên role mới
 * thấy đủ menu và vào được mọi route, chỉ vỡ khi API trả 403. Không phải lỗ hổng bảo
 * mật (backend đã chặn) nhưng là UX hỏng — và cùng kiểu sai đó sẽ lặp lại với mỗi role
 * thêm sau này.
 *
 * ĐẶT Ở `constants/` chứ không ở `state/` hay `helpers/`: đây là dữ liệu tĩnh, không
 * có state và không gọi API — đúng chỗ của `constants/shipping.js` (cũng là bản sao
 * của một registry backend). Ba nơi dùng nó (router guard, menu, thanh công cụ đơn
 * hàng) đều import trực tiếp, không đi qua Vuex, nên không nơi nào dựng lại danh
 * sách role cho riêng mình.
 *
 * QUY TẮC: mọi nhóm dưới đây là allow-list. Thêm role mới thì mặc định KHÔNG có gì;
 * phải chủ động khai vào nhóm tương ứng. Đừng viết lại thành "trừ role X ra".
 *
 * VIẾT BẰNG CommonJS (`.cjs`) để `node --test` require thẳng được (cùng khuôn
 * `helpers/shipFormStores.cjs`): luật khoá vùng ở đây có một bất biến chống vòng lặp
 * redirect cần test canh, mà bản ESM thì test node thuần không nạp nổi. `roles.js` bên
 * cạnh chỉ là lớp vỏ re-export cho phía Vue import như cũ.
 */

const ROLE_ADMIN = "admin";
const ROLE_STAFF = "staff";
const ROLE_PRINTER = "printer";
const ROLE_ACCOUNTANT = "accountant";
const ROLE_FOREMAN = "foreman";
const ROLE_SECRETARY = "secretary";
const ROLE_SUPER_ADMIN = "super_admin";
// Nhân viên vận đơn (chốt 2026-10-08) — gương `ROLE_SHIPPING_CLERK` ở backend.
const ROLE_SHIPPING_CLERK = "shipping_clerk";

/**
 * Role mang quyền quản trị — gương của `ADMIN_ROLES` ở backend. super_admin là admin
 * CỘNG THÊM (quản lý thư ký, công nợ CTV): nhóm nào dưới đây có ROLE_ADMIN thì PHẢI
 * có ROLE_SUPER_ADMIN (có test canh). Kiểm tra "là admin" thì dùng `isAdminRole`,
 * đừng so `=== ROLE_ADMIN`.
 */
const ADMIN_ROLES = Object.freeze([ROLE_ADMIN, ROLE_SUPER_ADMIN]);

/**
 * Nhãn tiếng Việt của từng role, hiện trên giao diện (dropdown chọn vai trò khi thêm
 * nhân viên…).
 *
 * ĐẶT CẠNH các hằng ROLE_* chứ không nằm trong màn hình dùng nó: bảng này trước đây
 * viết tay trong `views/apps/ecommerce/staff.vue`, nên khi thêm role mới mà quên khai
 * ở đó thì dropdown rơi về fallback `r.name` và hiện tên role tiếng Anh lẫn giữa các
 * nhãn tiếng Việt. Để chung chỗ với chính danh sách role thì lần thêm role sau khó
 * quên hơn.
 *
 * Khoá là TÊN role (ổn định), không phải id — id là auto-increment nên khác nhau giữa
 * các môi trường.
 */
const ROLE_LABELS = Object.freeze({
  [ROLE_ADMIN]: "Quản trị",
  [ROLE_STAFF]: "Nhân viên",
  [ROLE_PRINTER]: "Nhân viên in",
  [ROLE_ACCOUNTANT]: "Kế toán",
  [ROLE_FOREMAN]: "Quản đốc",
  [ROLE_SECRETARY]: "Thư ký",
  [ROLE_SUPER_ADMIN]: "Quản trị cấp cao",
  [ROLE_SHIPPING_CLERK]: "Nhân viên vận đơn",
});

/** Nhãn tiếng Việt của role; role lạ (chưa khai ở trên) thì trả về chính tên role. */
const roleLabel = (roleName) => ROLE_LABELS[roleName] || roleName || "";

/**
 * Role làm nghiệp vụ (đơn hàng, khách hàng, sản phẩm…). Gương của `BUSINESS_ROLES`
 * ở backend — sửa một bên thì sửa bên kia, nếu không menu sẽ hiện những mục mà API
 * trả 403.
 */
const BUSINESS_ROLES = Object.freeze([...ADMIN_ROLES, ROLE_STAFF, ROLE_PRINTER, ROLE_SHIPPING_CLERK]);

/**
 * Gương của `SALES_ROLES` ở backend: khách hàng, tổng quan, đánh giá, tem vận đơn,
 * thấy SĐT/địa chỉ khách. = BUSINESS_ROLES trừ `printer`.
 */
const SALES_ROLES = Object.freeze([...ADMIN_ROLES, ROLE_STAFF]);

/**
 * Gương `SHIPPING_LABEL_ROLES` ở backend: in tem vận đơn + PUT đơn = bán hàng + nhân
 * viên vận đơn. Ở FE cũng là tập được thấy SĐT/địa chỉ khách trên màn hình đơn
 * (nhân viên vận đơn cần chúng để làm vận đơn; nhân viên in thì không).
 */
const SHIPPING_LABEL_ROLES = Object.freeze([...SALES_ROLES, ROLE_SHIPPING_CLERK]);

/** Gương `ORDER_MONEY_ROLES` backend: thấy số tiền của đơn. Mọi role trừ nhân viên in (chốt 2026-10-20). */
const ORDER_MONEY_ROLES = Object.freeze([
  ...SALES_ROLES, ROLE_ACCOUNTANT, ROLE_FOREMAN, ROLE_SECRETARY, ROLE_SHIPPING_CLERK,
]);

/** Gương `INVOICE_ROLES` backend: thấy/tải/tạo hoá đơn. CHỈ bán hàng (chốt 2026-10-20). */
const INVOICE_ROLES = SALES_ROLES;

/** Module kế toán ĐẦY ĐỦ `/accounting/*`, gồm cả phần NCC — cổng backend ACCOUNTING_ROLES. */
const ACCOUNTING_ROLES = Object.freeze([...ADMIN_ROLES, ROLE_ACCOUNTANT]);

/**
 * Phần công nợ KHÁCH HÀNG (`/accounting/receivables`, `/accounting/order-payments`) —
 * gương `RECEIVABLE_ROLES` ở backend. = ACCOUNTING_ROLES + thư ký (chốt 2026-10-03).
 * Thư ký KHÔNG vào phần NCC: hai route NCC vẫn gác bằng ACCOUNTING_ROLES.
 */
const RECEIVABLE_ROLES = Object.freeze([...ACCOUNTING_ROLES, ROLE_SECRETARY]);

/**
 * Ai thấy nhóm "Công Nợ Khách Hàng" thu gọn trên sidebar (`components/menu.vue`) —
 * quyền HIỂN THỊ menu, không phải cổng truy cập (cổng là RECEIVABLE_ROLES). Kế toán
 * KHÔNG có ở đây: họ giữ menu phẳng 4 mục như cũ (`canSeeDebtTabs`) — user chốt
 * 2026-10-03. Admin thường cũng không: hành vi cũ là không có mục kế toán trên menu
 * của họ. Phải là tập con của RECEIVABLE_ROLES — `roles.test.cjs` canh.
 *
 * TẠM ẨN (user chốt 2026-10-03): để rỗng nên không ai thấy nhóm này. Chỉ ẩn menu —
 * quyền truy cập (RECEIVABLE_ROLES, vùng khoá của thư ký) giữ nguyên. Bật lại:
 * `Object.freeze([ROLE_SUPER_ADMIN, ROLE_SECRETARY])`.
 */
const RECEIVABLE_GROUP_MENU_ROLES = Object.freeze([]);

/** Dashboard lịch giao hàng `/delivery-schedule`. */
const DELIVERY_SCHEDULE_ROLES = Object.freeze([...ADMIN_ROLES, ROLE_FOREMAN]);

/**
 * Module công nợ CTV `/partner-debts/*` — gương của `PARTNER_DEBT_ROLES` ở backend.
 *
 * Thư ký + super_admin. CỐ Ý KHÔNG có `ROLE_ADMIN`, khác mọi nhóm còn lại trong file
 * này. Đừng "sửa cho nhất quán": chủ doanh nghiệp quyết định mảng này tách hẳn, admin
 * thường và kế toán đều bị backend trả 403. Thêm admin vào đây chỉ làm menu hiện một
 * mục bấm vào là lỗi.
 */
const PARTNER_DEBT_ROLES = Object.freeze([ROLE_SECRETARY, ROLE_SUPER_ADMIN]);

/**
 * Nhật ký hoạt động `/ecommerce/audit-logs` — gương của `AUDIT_LOG_ROLES` ở backend.
 * CHỈ super_admin: nhật ký có cả thao tác công nợ CTV của thư ký, mở cho admin thường
 * là lọt đúng phần admin không được xem. CỐ Ý không có `ROLE_ADMIN`.
 */
const AUDIT_LOG_ROLES = Object.freeze([ROLE_SUPER_ADMIN]);

/**
 * Khách riêng (bật/tắt + trang Công Nợ Khách Riêng) — gương `PRIVATE_DEBT_ROLES` ở
 * backend. CHỈ super_admin (user chốt 2026-10-11): admin thường không được thấy khách
 * nào là khách riêng. CỐ Ý không có `ROLE_ADMIN`, giống AUDIT_LOG_ROLES.
 */
const PRIVATE_DEBT_ROLES = Object.freeze([ROLE_SUPER_ADMIN]);

/**
 * Ai được đặt/sửa giá gốc sản phẩm trong bảng giá.
 * Gương của `PRICE_WRITE_ROLES` ở backend.
 */
const PRICE_WRITE_ROLES = ADMIN_ROLES;

/**
 * Ai được đặt/sửa đơn giá từng dòng hàng trong đơn. Gương của
 * `ORDER_ITEM_PRICE_WRITE_ROLES` ở backend.
 */
const ORDER_ITEM_PRICE_WRITE_ROLES = Object.freeze([...ADMIN_ROLES, ROLE_STAFF]);

/**
 * Ai được đổi trạng thái thanh toán và số tiền đã thanh toán của đơn. Gương của
 * `PAYMENT_WRITE_ROLES` ở backend — kế toán và thư ký có tên dù họ bị khoá vùng và
 * làm việc đó qua tab Thanh toán đơn hàng.
 */
const PAYMENT_WRITE_ROLES = Object.freeze([...ADMIN_ROLES, ROLE_ACCOUNTANT, ROLE_SECRETARY]);

/**
 * Ai được khai tiền thu hộ (COD) khác số hệ thống tự tính. Gương của
 * `COD_WRITE_ROLES` ở backend — tách khỏi `PAYMENT_WRITE_ROLES`: staff có quyền
 * COD nhưng không có quyền payment; kế toán thì ngược lại.
 */
const COD_WRITE_ROLES = Object.freeze([...ADMIN_ROLES, ROLE_STAFF]);

/** Ai được bật Freeship khi tạo/sửa đơn hoặc gửi vận đơn. Gương của `FREESHIP_WRITE_ROLES`. */
const FREESHIP_WRITE_ROLES = ADMIN_ROLES;

/**
 * Thao tác GHI HÀNG LOẠT trên danh sách đơn (gửi vận đơn, xoá, đổi trạng thái…).
 * Hẹp hơn BUSINESS_ROLES: `printer` chỉ được ghi trên đơn được giao, mà các nút này
 * chạy trên cả đơn không được giao nên backend sẽ trả 403 giữa chừng.
 */
const ORDER_BULK_ACTION_ROLES = Object.freeze([...ADMIN_ROLES, ROLE_STAFF]);

/**
 * Gửi VC hàng loạt + Chuyển trạng thái hàng loạt: ORDER_BULK_ACTION_ROLES + nhân viên
 * vận đơn (chốt 2026-10-08). Nhân viên vận đơn làm trên MỌI đơn; đơn ngoài vùng của
 * họ (trước Đang SX, đã huỷ) bị bỏ qua và báo số đơn — không 403 giữa chừng.
 */
const ORDER_BULK_SHIP_ROLES = Object.freeze([...ORDER_BULK_ACTION_ROLES, ROLE_SHIPPING_CLERK]);

/**
 * Role bị KHOÁ trong đúng một khu vực: vào bất kỳ route nào khác thì bị đá về trang
 * chủ của khu vực đó. Mỗi mục là { prefix | prefixes, home, confinePublicRoutes? } —
 * `prefixes` (mảng) dành cho vùng gồm nhiều nhánh route rời nhau; đọc qua `areaPrefixes`.
 *
 * Gom thành bảng thay vì viết hai khối `if` giống nhau trong router: khối thứ hai
 * copy từ khối thứ nhất là chỗ dễ quên nhất — quên đúng một dấu `!` là thành vòng
 * lặp redirect, và triệu chứng (trang trắng) không chỉ về dòng code nào cả.
 *
 * `confinePublicRoutes: true` (chỉ quản đốc) nghĩa là role này bị khoá vùng NGAY CẢ
 * trên route công khai (authRequired: false, ví dụ /production-board, /tv-dashboard,
 * /demo/:code) khi đã đăng nhập — quản đốc quét QR trong ca vẫn phải ở lại lịch giao
 * hàng. Kế toán KHÔNG bật cờ này: họ đã quen xem được các trang công khai đó từ
 * trước khi có role quản đốc, tắt cờ để giữ nguyên hành vi cũ cho role này.
 */
const CONFINED_ROLE_AREAS = Object.freeze({
  [ROLE_ACCOUNTANT]: { prefix: "/accounting/", home: "/accounting/receivables" },
  [ROLE_FOREMAN]: {
    prefix: "/delivery-schedule",
    home: "/delivery-schedule",
    confinePublicRoutes: true,
  },
  // Thư ký: công nợ CTV + công nợ khách hàng (2 tab, chốt 2026-10-03). Họ KHÔNG nằm
  // trong `BUSINESS_ROLES` nên mọi API nghiệp vụ trả 403 — không khoá vùng thì đăng
  // nhập xong rơi vào "/" (Tổng quan) và thấy dashboard rỗng kèm lỗi. Liệt kê ĐÍCH
  // DANH hai route kế toán, không dùng cả "/accounting/": thế là lọt phần NCC.
  // KHÔNG bật `confinePublicRoutes`: giữ hành vi mặc định như kế toán, thư ký vẫn
  // mở được trang demo/TV xưởng nếu cần.
  [ROLE_SECRETARY]: {
    prefixes: ["/partner-debts", "/accounting/receivables", "/accounting/order-payments"],
    home: "/partner-debts",
  },
  // Nhân viên in chỉ làm trên danh sách + chi tiết đơn (chốt 2026-10-02). KHÔNG bật
  // confinePublicRoutes: họ vẫn mở ảnh demo /demo/:code và bảng xưởng.
  [ROLE_PRINTER]: {
    prefixes: ["/ecommerce/orders", "/ecommerce/order-details"],
    home: "/ecommerce/orders",
  },
  // Nhân viên vận đơn: như nhân viên in + form sửa đơn (chỉ bật khối vận chuyển/địa
  // chỉ — chốt 2026-10-08). KHÔNG có /ecommerce/add-order.
  [ROLE_SHIPPING_CLERK]: {
    prefixes: ["/ecommerce/orders", "/ecommerce/order-details", "/ecommerce/edit-order"],
    home: "/ecommerce/orders",
  },
});

/** Danh sách tiền tố của một vùng — vùng cũ khai `prefix`, vùng mới có thể khai `prefixes`. */
const areaPrefixes = (area) => area.prefixes || [area.prefix];

/** Role có bị khoá vùng ngay cả trên route công khai không (xem giải thích ở trên). */
const confinesOnPublicRoutes = (roleName) => !!CONFINED_ROLE_AREAS[roleName]?.confinePublicRoutes;

/** Có role này trong danh sách cho phép không. */
const roleAllowed = (roleName, allowed) => !!roleName && allowed.includes(roleName);

/** Role có quyền quản trị (admin hoặc super_admin) không. */
const isAdminRole = (roleName) => roleAllowed(roleName, ADMIN_ROLES);

/**
 * Route mà MỌI role vào được, kể cả role bị khoá vùng.
 *
 * Không có danh sách này thì `accountant` bị đá khỏi /pages/profile-setting (nó không
 * nằm trong /accounting/) — tức là role đó KHÔNG ĐỔI ĐƯỢC MẬT KHẨU, trong khi menu vẫn
 * hiện link: bấm vào là bật ngược về trang chủ khu vực, không một dòng thông báo.
 */
const SHARED_ROUTE_PREFIXES = Object.freeze(["/pages/profile-setting", "/logout"]);

const isSharedRoute = (path) =>
  SHARED_ROUTE_PREFIXES.some((prefix) => path === prefix || path.startsWith(`${prefix}/`));

/**
 * Role bị khoá vùng đang đi ra ngoài vùng ⇒ trả về đường dẫn phải đá về; `null` = cho qua.
 *
 * BẤT BIẾN: `home` của mỗi khu vực PHẢI nằm trong một `prefix` của chính nó, nếu không lần
 * redirect nào cũng lại rơi ra ngoài vùng và guard đá vòng quanh vô tận (triệu chứng chỉ
 * là trang trắng, không chỉ về dòng code nào). Có test canh bất biến này.
 */
const confinedRedirect = (roleName, path) => {
  const area = CONFINED_ROLE_AREAS[roleName];
  if (!area) return null;
  if (typeof path !== "string" || path === "") return area.home;
  const inside = areaPrefixes(area).some((prefix) => {
    const childPrefix = prefix.endsWith("/") ? prefix : `${prefix}/`;
    return path === prefix || path.startsWith(childPrefix);
  });
  if (inside || isSharedRoute(path)) return null;
  return area.home;
};

module.exports = {
  ACCOUNTING_ROLES,
  ADMIN_ROLES,
  AUDIT_LOG_ROLES,
  COD_WRITE_ROLES,
  FREESHIP_WRITE_ROLES,
  INVOICE_ROLES,
  PAYMENT_WRITE_ROLES,
  PRICE_WRITE_ROLES,
  ROLE_LABELS,
  BUSINESS_ROLES,
  CONFINED_ROLE_AREAS,
  DELIVERY_SCHEDULE_ROLES,
  ORDER_ITEM_PRICE_WRITE_ROLES,
  ORDER_BULK_ACTION_ROLES,
  ORDER_BULK_SHIP_ROLES,
  ORDER_MONEY_ROLES,
  PARTNER_DEBT_ROLES,
  PRIVATE_DEBT_ROLES,
  RECEIVABLE_GROUP_MENU_ROLES,
  RECEIVABLE_ROLES,
  ROLE_ACCOUNTANT,
  ROLE_ADMIN,
  ROLE_FOREMAN,
  ROLE_PRINTER,
  ROLE_SECRETARY,
  ROLE_SHIPPING_CLERK,
  ROLE_STAFF,
  ROLE_SUPER_ADMIN,
  SALES_ROLES,
  SHARED_ROUTE_PREFIXES,
  SHIPPING_LABEL_ROLES,
  areaPrefixes,
  confinedRedirect,
  confinesOnPublicRoutes,
  isAdminRole,
  isSharedRoute,
  roleAllowed,
  roleLabel,
};
